// src/pages/Languages/index.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../contexts/LanguageContext";
import { fetchLanguages, deleteLanguage, setCurrentPage } from "../../redux/slices/languagesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import NewLanguageModal from "../../components/Languages/NewLanguageModal";
import ViewEditLanguageModal from "../../components/Languages/ViewEditLanguageModal";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import Pagination from "../../components/Common/Pagination";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

function LanguagesPage() {
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  const { languages, loading, error, total, totalPages, currentPage } = useSelector(
    (state) => state.languages
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewEditModalOpen, setIsViewEditModalOpen] = useState(false);
  const [languageToView, setLanguageToView] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadingKey, setDownloadingKey] = useState(null);

  const limit = 10;

  useEffect(() => {
    dispatch(fetchLanguages({ currentPage, limit }));
  }, [dispatch, currentPage]);

  // Filter languages based on search
  const filteredLanguages = languages.filter((lang) => {
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      return (
        lang.name?.toLowerCase().includes(lowerSearch) ||
        lang.key?.toLowerCase().includes(lowerSearch)
      );
    }
    return true;
  });

  const openDeleteModal = (language) => {
    setLanguageToDelete(language);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!languageToDelete) return;

    setIsDeleting(true);
    dispatch(deleteLanguage(languageToDelete.key))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: "Language deleted successfully!", type: "success" }));
        setIsDeleteModalOpen(false);
        setLanguageToDelete(null);
      })
      .catch((error) => {
        dispatch(showNotification({ message: error || "Failed to delete language", type: "error" }));
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleView = (language) => {
    setLanguageToView(language);
    setIsViewEditModalOpen(true);
  };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  // Download language JSON file to user's computer
  const handleDownload = async (language) => {
    if (!language.url) {
      dispatch(showNotification({ message: "No URL available for this language", type: "error" }));
      return;
    }

    setDownloadingKey(language.key);
    try {
      const response = await fetch(language.url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Create a formatted JSON string
      const jsonString = JSON.stringify(data, null, 2);
      
      // Create a blob and download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${language.key}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(showNotification({ message: `${language.name} (${language.key}.json) downloaded successfully!`, type: "success" }));
    } catch (error) {
      console.error("Download error:", error);
      dispatch(showNotification({ message: `Failed to download ${language.name}: ${error.message}`, type: "error" }));
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <div>
      {/* Table Container */}
      <div className="mt-4 bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Dark Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-white font-semibold text-lg">{t("languages.title")}</h2>
              <span className="text-white font-semibold">({total})</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("common.search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
              />
            </div>

            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-dashboardCoral rounded text-white text-sm font-medium"
            >
              <PlusIcon className="w-[18px] h-[18px]" />
              <span>{t("common.addNew")}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
            <span className="ml-2">{t("languages.loading")}</span>
          </div>
        ) : error ? (
          <p className="p-4 text-red-500">Error: {error}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dashboardStroke">
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">
                    {t("languages.col.key")}
                  </th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">
                    {t("col.name")}
                  </th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">
                    {t("col.status")}
                  </th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">
                    {t("col.createdAt")}
                  </th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">
                    {t("col.updatedAt")}
                  </th>
                  <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">
                    {t("col.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredLanguages.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      {t("languages.noFound")}
                    </td>
                  </tr>
                ) : (
                  filteredLanguages.map((language, index) => {
                    return (
                    <tr
                      key={language._id}
                      className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                        index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                      }`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                          {language.key}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {language.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${
                          language.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {tStatus(language.status || 'active')}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {new Date(language.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {new Date(language.updatedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => handleDownload(language)}
                            disabled={downloadingKey === language.key}
                            className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                            title="Download JSON"
                          >
                            {downloadingKey === language.key ? (
                              <div className="w-6 h-6 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              </div>
                            ) : (
                              <ArrowDownTrayIcon className="w-6 h-6 text-blue-600" strokeWidth={1.3} />
                            )}
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button
                            onClick={() => handleView(language)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="View & Edit"
                          >
                            <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button
                            onClick={() => openDeleteModal(language)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-dashboardStroke">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <NewLanguageModal isOpen={isAddModalOpen} setIsOpen={setIsAddModalOpen} />
      <ViewEditLanguageModal
        isOpen={isViewEditModalOpen}
        setIsOpen={setIsViewEditModalOpen}
        language={languageToView}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t("deleteConfirm.language.title")}
        message={t("deleteConfirm.language.message").replace("{name}", languageToDelete?.name || "")}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        confirmText={t("deleteConfirm.delete")}
        cancelText={t("deleteConfirm.cancel")}
      />
    </div>
  );
}

export default LanguagesPage;
